// components/Contact.js
import React, { useEffect, useRef, useState } from 'react';
import './CSS/Contact.css';

const Contact = () => {
  const sectionRef = useRef(null);
  const [appear, setAppear] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const currentSection = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAppear(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://formsubmit.co/ajax/alidarbar.inn@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _subject: `New Contact Form Submission: ${formData.subject}`,
          _template: 'table',
          _captcha: 'false',
          _autoresponse: `Thank you for contacting Ali Darbar! We have received your message and will get back to you within 24 hours.`
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        
        setTimeout(() => {
          setSubmitStatus(null);
        }, 5000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openGoogleMaps = () => {
    const mapsUrl = `https://maps.app.goo.gl/oVtri63wRWuEr5yn6`;
    window.open(mapsUrl, '_blank');
  };

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  return (
    <section id="contact" className="section contact-section" ref={sectionRef}>
      <div className="container">
        <div className="section-title">
          <h2>Visit Us Today</h2>
          <p>We'd love to hear from you. Reach out to us for reservations or inquiries.</p>
        </div>
        
        <div className="contact-content">
          {/* Left Side - Contact Info & Map */}
          <div className={`contact-info slide-in-left${appear ? ' appear' : ''}`}>
            <div className="contact-card">
              <h3>Get In Touch</h3>
              
              <div className="contact-item animated-item">
                <div className="contact-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className="contact-details">
                  <h4>Address</h4>
                  <p>Shop no. 2 parampujya Ravishankarji marg, madina nagar, wadala, beside Madina lawns, </p>
                  <p>opposite Bharat petroleum, nashik, maharashta - 422006.</p>
                  <button className="map-link" onClick={openGoogleMaps}>
                    <i className="fas fa-directions"></i> Get Directions
                  </button>
                </div>
              </div>
              
              <div className="contact-item animated-item">
                <div className="contact-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <div className="contact-details">
                  <h4>Phone</h4>
                  <p>+91 7030808095</p>
                  <p>+91 7030808025</p>
                  <p className="contact-note">Call us for reservations</p>
                </div>
              </div>
              
              <div className="contact-item animated-item">
                <div className="contact-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="contact-details">
                  <h4>Email</h4>
                  <p>alidarbar.inn@gmail.com</p>
                  <p className="contact-note">We respond within 24 hours</p>
                </div>
              </div>
              
              <div className="contact-item animated-item">
                <div className="contact-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="contact-details">
                  <h4>Opening Hours</h4>
                  <div className="hours-list">
                    <div className="hour-item">
                      <span>Monday - Friday</span>
                      <span>11:00 AM - 10:00 PM</span>
                    </div>
                    <div className="hour-item">
                      <span>Saturday - Sunday</span>
                      <span>11:00 AM - 11:00 PM</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Google Maps */}
              <div className="live-map-container animated-item">
                <div className="map-header">
                  <h4>
                    <i className="fas fa-map-marker-alt"></i>
                    Find Us Here
                  </h4>
                  <button className="map-fullscreen-btn" onClick={openGoogleMaps}>
                    <i className="fas fa-expand"></i>
                  </button>
                </div>
                
                <div className={`map-wrapper ${mapLoaded ? 'loaded' : ''}`}>
                  {!mapLoaded && (
                    <div className="map-loader">
                      <div className="loader-spinner"></div>
                      <p>Loading Map...</p>
                    </div>
                  )}
                  
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d29999.672028742858!2d73.76172959804532!3d19.968226277860722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bddeb0010ff0c35%3A0x9533195b1d4f6389!2sAli%20Darbar!5e0!3m2!1sen!2sin!4v1759072857423!5m2!1sen!2sin"
                    width="100%"
                    height="300"
                    style={{ border: 0, borderRadius: '10px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    onLoad={handleMapLoad}
                    title="Ali Darbar Restaurant Location"
                    className="live-map-iframe"
                  ></iframe>

                </div>
                
                <div className="map-actions">
                  <button className="action-btn" onClick={openGoogleMaps}>
                    <i className="fas fa-car"></i>
                    Drive
                  </button>
                  <button className="action-btn" onClick={openGoogleMaps}>
                    <i className="fas fa-walking"></i>
                    Walk
                  </button>
                  <button className="action-btn" onClick={openGoogleMaps}>
                    <i className="fas fa-train"></i>
                    Transit
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className={`contact-form slide-in-right${appear ? ' appear' : ''}`}>
            <div className="form-header">
              <h3>Send us a Message</h3>
              <p>Fill out the form below and we'll get back to you soon</p>
            </div>
            
            <form onSubmit={handleSubmit}>
              <input 
                type="hidden" 
                name="_captcha" 
                value="false" 
              />
              <input 
                type="hidden" 
                name="_template" 
                value="table" 
              />
              
              <div className="form-group animated-input">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  required 
                />
                <i className="fas fa-user input-icon"></i>
              </div>

              <div className="form-group animated-input">
                <label htmlFor="number">Phone Number *</label>
                <input 
                  type="number" 
                  id="number" 
                  name="number" 
                  placeholder="Enter your Phone Number"
                  value={formData.number}
                  onChange={handleChange}
                  required 
                />
                <i className="fas fa-phone input-icon"></i>
              </div>
              
              <div className="form-group animated-input">
                <label htmlFor="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
                <i className="fas fa-envelope input-icon"></i>
              </div>
              
              <div className="form-group animated-input">
                <label htmlFor="subject">Subject *</label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  placeholder="What is this regarding?"
                  value={formData.subject}
                  onChange={handleChange}
                  required 
                />
                <i className="fas fa-tag input-icon"></i>
              </div>
              
              <div className="form-group animated-input">
                <label htmlFor="message">Your Message *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows="6" 
                  placeholder="Tell us how we can help you..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
                <i className="fas fa-comment input-icon textarea-icon"></i>
              </div>

              {submitStatus === 'success' && (
                <div className="submit-status success">
                  <i className="fas fa-check-circle"></i>
                  <span>Thank you! Your message has been sent successfully.</span>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="submit-status error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>Sorry, there was an error sending your message. Please try again.</span>
                </div>
              )}

              <button 
                type="submit" 
                className={`btn submit-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Send Message
                  </>
                )}
              </button>

              <div className="form-footer">
                <p>
                  <i className="fas fa-shield-alt"></i>
                  Your information is safe with us. We respect your privacy.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
